import React, { useId, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchHistory } from '../utils/searchHistory';

interface SearchHistoryInputProps {
  // Which page's history to use, e.g. 'purchase_orders'. Lowercase letters, digits, underscores.
  scope: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

// Search box that remembers the user's searches per page (synced across devices)
// and offers them back in a dropdown. A keyword is saved on Enter, on picking it
// from the list, or when the box loses focus; the ✕ clears the box without saving.
const SearchHistoryInput: React.FC<SearchHistoryInputProps> = ({ scope, value, onChange, placeholder, className = '' }) => {
  const { user } = useAuth();
  const { items, add, remove, clear } = useSearchHistory(scope, user?.id);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const query = value.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query) return items;
    return items.filter(i => {
      const term = i.term.toLowerCase();
      return term !== query && term.includes(query);
    });
  }, [items, query]);

  const showDropdown = isOpen && suggestions.length > 0;

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const select = (term: string) => {
    onChange(term);
    add(term);
    close();
  };

  const handleClear = () => {
    onChange('');
    setActiveIndex(-1);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (suggestions.length === 0) return;
      e.preventDefault();
      setIsOpen(true);
      const last = suggestions.length - 1;
      setActiveIndex(i => (e.key === 'ArrowDown' ? (i < last ? i + 1 : 0) : (i > 0 ? i - 1 : last)));
    } else if (e.key === 'Enter') {
      if (showDropdown && suggestions[activeIndex]) {
        e.preventDefault();
        select(suggestions[activeIndex].term);
      } else {
        add(value);
        close();
      }
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
        <span className="material-symbols-outlined text-[20px]">search</span>
      </span>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-10 text-sm focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] outline-none"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setActiveIndex(-1);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onBlur={() => {
          add(value);
          close();
        }}
        onKeyDown={handleKeyDown}
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          title="Clear search"
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 transition-colors"
          // Keep focus in the input so clearing doesn't count as a finished search.
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      )}
      {showDropdown && (
        <div
          className="absolute left-0 right-0 z-30 mt-2 rounded-lg border border-slate-200 bg-white py-2 shadow-lg"
          // Clicks inside the dropdown must not blur the input.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between px-3 pb-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Recent Searches</p>
            <button
              type="button"
              className="text-xs font-medium text-[#136bce] hover:text-blue-700 hover:underline"
              onClick={clear}
            >
              Clear all
            </button>
          </div>
          <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto">
            {suggestions.map((item, index) => (
              <li
                key={item.id}
                role="option"
                aria-selected={index === activeIndex}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-slate-700 ${index === activeIndex ? 'bg-slate-100' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(item.term)}
              >
                <span className="material-symbols-outlined text-[18px] text-slate-400">history</span>
                <span className="flex-1 truncate">{item.term}</span>
                <button
                  type="button"
                  aria-label={`Remove "${item.term}" from recent searches`}
                  title="Remove"
                  className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(item.id);
                  }}
                >
                  <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchHistoryInput;
